import styled from 'styled-components';
import type { GroupRateKind } from '@shared/api';
import { pluralizeRu } from '@shared/lib/formatFile';
import { Notice, fleetLeading, fleetType, formatCount, formatIsoDate, typo } from '@entities/fleet';

/** Счётчики — как графы ведомости: ячейки через 1 px, каждая — фильтр таблицы. */
const Strip = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(${({ theme }) => theme.spacing(40)}, 1fr));
  gap: 1px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.border};
  overflow: hidden;
`;

const Cell = styled.button`
  display: grid;
  gap: ${({ theme }) => theme.spacing(0.5)};
  min-width: 0;
  padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(4)}`};
  border: 0;
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: background ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  & > .n {
    font-size: ${fleetType.title};
    font-weight: 600;
    line-height: ${fleetLeading.tight};
    letter-spacing: -0.01em;
    font-variant-numeric: tabular-nums;
  }

  & > .l {
    font-size: ${fleetType.small};
    line-height: ${fleetLeading.tight};
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  &[data-alert='true'] > .n {
    color: ${({ theme }) => theme.colors.status.danger};
  }

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
  }

  &[aria-pressed='true'] {
    background: ${({ theme }) => theme.colors.surface};
    box-shadow: inset 0 -2px 0 ${({ theme }) => theme.colors.primary};
  }

  /* Ячейки в обрезанной полосе: кольцо фокуса — внутрь, иначе его срежет край. */
  &:focus-visible {
    border-radius: 0;
    box-shadow: inset 0 0 0 2px ${({ theme }) => theme.colors.primary};
  }

  &[aria-pressed='true']:focus-visible {
    box-shadow:
      inset 0 0 0 2px ${({ theme }) => theme.colors.primary},
      inset 0 -4px 0 ${({ theme }) => theme.colors.primary};
  }
`;

const Explain = styled(Notice)`
  margin-top: ${({ theme }) => theme.spacing(3)};
`;

interface RatesSummaryProps {
  /** Объект — три счётчика; по умолчанию для холдинга — «есть» и «нет». */
  scope: 'project' | 'default';
  counts: Readonly<Record<GroupRateKind, number>>;
  filter: GroupRateKind | null;
  onFilter: (kind: GroupRateKind | null) => void;
  on: string;
}

const LABEL: Readonly<Record<'project' | 'default', Readonly<Record<GroupRateKind, string>>>> = {
  project: { project: 'ставка объекта', default: 'по умолчанию', missing: 'нет ставки' },
  default: { project: '', default: 'есть ставка', missing: 'нет ставки по умолчанию' },
};

/**
 * Сколько групп со своей ставкой, со ставкой по умолчанию и без ставки на
 * дату. Без ставки БДР объекта не утвердится — это объяснено под счётчиком.
 */
export function RatesSummary({ scope, counts, filter, onFilter, on }: RatesSummaryProps) {
  const kinds: GroupRateKind[] =
    scope === 'project' ? ['project', 'default', 'missing'] : ['default', 'missing'];
  const missing = counts.missing;
  const groupsWord = (n: number) => pluralizeRu(n, ['группа', 'группы', 'групп']);
  const date = formatIsoDate(on);

  return (
    <div>
      <Strip
        role="group"
        aria-label="Сколько групп с какой ставкой — нажмите, чтобы показать только их"
      >
        {kinds.map((k) => (
          <Cell
            key={k}
            type="button"
            aria-pressed={filter === k}
            data-alert={k === 'missing' && counts[k] > 0 ? 'true' : undefined}
            onClick={() => onFilter(filter === k ? null : k)}
          >
            <span className="n">{formatCount(counts[k])}</span>
            <span className="l">{LABEL[scope][k]}</span>
          </Cell>
        ))}
      </Strip>
      {missing > 0 ? (
        <Explain $tone="danger" role="status">
          <span className="text">
            <strong>
              {formatCount(missing)} {groupsWord(missing)} без ставки
              {scope === 'default' ? ' по умолчанию' : ''} на {date}.
            </strong>{' '}
            {typo(
              scope === 'project'
                ? 'Часы механизаторов этих групп в БДР объекта будут, а стоимости техники — нет. Такой БДР не утвердить, пока ставку не зададут: для объекта или по умолчанию для холдинга.'
                : 'На объектах, где у этих групп нет своей ставки, стоимость техники не посчитается — БДР такого объекта не утвердится.',
            )}
          </span>
        </Explain>
      ) : (
        counts.project + counts.default > 0 && (
          <Explain $tone="success" role="status">
            <span className="text">
              {typo(
                scope === 'project'
                  ? `У всех групп есть ставка на ${date} — БДР объекта посчитает стоимость техники.`
                  : `У всех групп есть ставка по умолчанию на ${date}.`,
              )}
            </span>
          </Explain>
        )
      )}
    </div>
  );
}
